import * as queryString from 'query-string';
import { isValidUriElement } from './validate';

/**
 * A vanity url must include at least 5 parts in its host:
 *
 * Ex: https://j1dev.apps.dev.jupiterone.io so length of 5
 * For localhost its: https://j1dev.localhost:8080 so length of 2
 */
const VANITY_HOST_PART_LENGTH = isLocal() ? 2 : 5;

interface BuildVanityUrlInput {
  accountId: string;
  subdomain?: string;
  location?: Location;
}

export function getOriginUri() {
  return (
    window.location.protocol +
    '//' +
    window.location.hostname +
    (window.location.port ? ':' + window.location.port : '')
  );
}

/**
 * Returns `true` if on localhost otherwise `false`
 */
export function isLocal(host = window.location.hostname) {
  return host.endsWith('localhost') || host === '127.0.0.1' || host === '';
}

/**
 * apps.dev.jupiterone.io -> dev
 * apps.us.jupiterone.io -> us
 */
export function getHostEnv(host = window.location.hostname) {
  if (isLocal(host)) {
    return 'dev';
  }

  const hostParts = host.split('.');
  // if the vanity URL is present, E.G. j1dev.apps.dev.jupiterone.io, then
  // return the correct index of the hostParts
  return isAccountIdPresentInUrl(hostParts) ? hostParts[2] : hostParts[1];
}

/**
 * Returns the app name from the URL
 *
 * https://apps.dev.jupiterone.io/galaxy -> galaxy
 * https://apps.dev.jupiterone.io/integrations/1234 -> integrations
 * https://apps.dev.jupiterone.io/ -> ''
 */
export function getAppFromUrl(pathname = window.location.pathname) {
  const appParts = pathname.split('/');
  return appParts[1] || '';
}

export function stringifyParams(params: any) {
  if (params && Object.keys(params).length) {
    return queryString.stringify(params);
  }

  return '';
}
export function stringifyArrayParams(params: string[], key: string) {
  return queryString.stringify({ [key]: params });
}

export function getHostParts(location = window.location) {
  return location.host.split('.');
}

export function buildHostFromParts(hostParts: string[]) {
  return hostParts.join('.');
}

export function isAccountIdPresentInUrl(hostParts: string[]) {
  return hostParts.length >= VANITY_HOST_PART_LENGTH;
}

/**
 * Returns the accountId if the user is currently using a vanity URL for SSO
 *
 * e.g. https://j1dev.apps.dev.jupiterone.io -> j1dev
 */
export function getAccountIdFromUrl(
  hostParts = getHostParts()
): string | undefined {
  return isAccountIdPresentInUrl(hostParts) ? hostParts[0] : undefined;
}

/**
 * When switching accounts, a vanity URL should be generated for the new account
 * so that SSO can be used for the alternative account.
 *
 * @param accountId
 * @param location
 */
export function buildVanityUrl(input: BuildVanityUrlInput) {
  const { accountId, subdomain, location = window.location } = input;

  const { protocol, host, pathname } = location;
  const hostParts = getHostParts(location);
  const hostSubdomain = subdomain || accountId;
  if (isAccountIdPresentInUrl(hostParts)) {
    hostParts[0] = hostSubdomain;
    const newHost = buildHostFromParts(hostParts);
    return `${protocol}//${newHost}${pathname}`;
  } else {
    return `${protocol}//${hostSubdomain}.${host}${pathname}`;
  }
}

export function baseEnvUrl(uri: string, baseSubdomain = 'apps') {
  const hostParts = getHostParts();
  const host =
    hostParts.length === 1
      ? hostParts.join('.')
      : hostParts[0] === baseSubdomain && hostParts[1] !== baseSubdomain
      ? hostParts.join('.')
      : hostParts.slice(1).join('.');
  return `${window.location.protocol}//${host}${
    uri.startsWith('/') ? uri : '/' + uri
  }`;
}

export function buildValidUriElement(str: string = '') {
  if (!str || isValidUriElement(str)) return str;
  const newStr = str.replace(/[^\w\s-]/gi, '-');
  return newStr.replace(/-+/g, '-');
}

export const navigateAbsoluteURL = (url: string) => {
  window.location.pathname = url;
};
